/* eslint-disable complexity */
import { DateInputFormik } from '@strata/react-date-time';
import { WarningDialog } from '@strata/react-dialog';
import { FormContainer } from '@strata/react-form-container';
import { TextInputFormik } from '@strata/react-inputs';
import { RadioButtonGroupFormik } from '@strata/react-radio-button-group';
import { SuccessToast } from '@strata/react-toast-message';
import { dialogStrings, validationStrings } from '@strata/strata-ui-strings';
import { Formik } from 'formik';
import { useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import * as Yup from 'yup';

import { CREATE_ROUTE } from 'services/constants';
import { handleError } from 'services/ErrorManagement';
import { put, useGet } from 'services/Http';

export const CAGDetails = () => {
    const { segmentId, cagId, programEffectiveDate, goLiveId } = useParams();

    const navigate = useNavigate();

    const goHome = () => navigate(`/go-lives/${segmentId}`);
    const isCreate = cagId === CREATE_ROUTE;

    const segment = useGet(`/reference/payer_segments/${segmentId}`);

    const cag = useGet(
        !isCreate
            ? `/reference/payer_segments/${segmentId}/cag/${cagId}/${programEffectiveDate}`
            : null,
    );

    const [showWarning, setShowWarning] = useState(false);

    const upsertCAGDetails = async (values) => {
        try {
            await put(`/reference/payer_segments/${segmentId}/cag/${cagId}`, {
                go_live_id: goLiveId,
                client_name: values.clientName,
                group_name: values.groupName,
                carrier: values.carrier,
                account: values.account,
                benefit_group: values.benefitGroup,
                program_effective_date: values.programEffectiveDate,
                program_end_date: values.programEndDate,
                lob: values.lob,
                state: values.state,
                bin: values.bin,
                pcn: values.pcn,
                rx_group: values.rxGroup,
                has_copay_assistance: values.hasCopayAssistance,
                group_spread: values.groupSpread,
            });
            SuccessToast({
                message: `CAG ${isCreate ? 'Created' : 'Updated'} Successfully!`,
            });
            goHome();
        } catch (err) {
            handleError(`Error saving CAG details.`, err);
        }
    };

    return (
        <Formik
            initialValues={{
                carrier: cag?.carrier || undefined,
                groupName: cag?.group_name || undefined,
                account: cag?.account || undefined,
                benefitGroup: cag?.benefit_group || undefined,
                segmentName: segment?.segment_name,
                clientName: cag?.client_name || undefined,
                programEffectiveDate: cag?.program_effective_date || undefined,
                programEndDate: cag?.program_end_date || undefined,
                lob: cag?.lob || undefined,
                state: cag?.state || undefined,
                bin: cag?.bin || undefined,
                pcn: cag?.pcn || undefined,
                rxGroup: cag?.rx_group || undefined,
                hasCopayAssistance: cag?.has_copay_assistance ?? undefined,
                groupSpread: cag?.group_spread ?? undefined,
            }}
            enableReinitialize
            validationSchema={Yup.object({
                carrier: Yup.string()
                    .max(50, validationStrings.maxCharacter[50])
                    .required(validationStrings.requiredField.errorMessage),
                groupName: Yup.string(),
                clientName: Yup.string().required(),
                account: Yup.string(),
                benefitGroup: Yup.string(),
                programEffectiveDate: DateInputFormik.validator().required(
                    validationStrings.requiredField.errorMessage,
                ),
                programEndDate: DateInputFormik.validator().required(
                    validationStrings.requiredField.errorMessage,
                ),
                lob: Yup.string(),
                state: Yup.string(),
                bin: Yup.string(),
                pcn: Yup.string(),
                rxGroup: Yup.string(),
                hasCopayAssistance: Yup.bool(),
                groupSpread: Yup.bool(),
            })}
            onSubmit={(values) => upsertCAGDetails(values)}
        >
            {(formik) => (
                <>
                    <WarningDialog
                        dialogTitle={dialogStrings.unsavedChanges.title}
                        isOpen={showWarning}
                        onSecondaryButtonClick={() => setShowWarning(false)}
                        onPrimaryButtonClick={goHome}
                        dialogContent={<p>{dialogStrings.unsavedChanges.body}</p>}
                    />
                    <FormContainer
                        formTitle={`${isCreate ? 'Create' : 'Edit'} CAG`}
                        isLoading={!cag && !isCreate}
                        onFormSubmit={formik.handleSubmit}
                        isSubmitting={formik.isSubmitting || !(formik.dirty && formik.isValid)}
                        onSecondaryButtonClick={() => {
                            if (formik.dirty) {
                                setShowWarning(true);
                            } else {
                                goHome();
                            }
                        }}
                        formLeftContent={
                            <>
                                <TextInputFormik
                                    label="Payer Segment"
                                    id="segmentName"
                                    required
                                    disabled
                                />
                                <TextInputFormik label="Client Name" id="clientName" required />
                                <TextInputFormik label="Group Name" id="groupName" />
                                <TextInputFormik
                                    label="Carrier"
                                    id="carrier"
                                    disabled={!isCreate}
                                    required
                                />
                                <TextInputFormik
                                    label="Account"
                                    id="account"
                                    disabled={!isCreate}
                                    required
                                />
                                <TextInputFormik
                                    label="Group"
                                    id="benefitGroup"
                                    disabled={!isCreate}
                                    required
                                />
                                <DateInputFormik
                                    label="Program Start Date"
                                    id="programEffectiveDate"
                                    required
                                />
                                <DateInputFormik
                                    label="Program End Date"
                                    id="programEndDate"
                                    required
                                />
                                <TextInputFormik label="Line of Business" id="lob" />
                                <TextInputFormik label="State" id="state" />
                                <TextInputFormik label="BIN" id="bin" />
                                <TextInputFormik label="PCN" id="pcn" />
                                <TextInputFormik label="Rx Group" id="rxGroup" />
                                <RadioButtonGroupFormik
                                    name="hasCopayAssistance"
                                    label="Has Copay Assistance"
                                    helperText="Does this CAG have copay assistance?"
                                    options={['Yes', 'No']}
                                    selectedValue={(() => {
                                        if (formik.values.hasCopayAssistance == null) {
                                            return undefined;
                                        }
                                        return formik.values.hasCopayAssistance ? 'Yes' : 'No';
                                    })()}
                                    onChange={(value) => {
                                        void formik.setFieldValue(
                                            'hasCopayAssistance',
                                            value === 'Yes',
                                        );
                                    }}
                                />
                                <RadioButtonGroupFormik
                                    name="groupSpread"
                                    label="Group Spread"
                                    helperText="Does this CAG have spread pricing?"
                                    options={['Yes', 'No']}
                                    selectedValue={(() => {
                                        if (formik.values.groupSpread == null) {
                                            return undefined;
                                        }
                                        return formik.values.groupSpread ? 'Yes' : 'No';
                                    })()}
                                    onChange={(value) => {
                                        void formik.setFieldValue('groupSpread', value === 'Yes');
                                    }}
                                />
                            </>
                        }
                    />
                </>
            )}
        </Formik>
    );
};
